import { isDesktop } from '@/consts/device';
import getFile from '@/requests/cloud-storage/getFile';
import getMediaFromFlowId from '@/requests/magic-flow/getMediaFromFlowId';
import { getAppStore, setAppStore } from '@/store';
import { RightPanelTab } from '@/store/navigation/types';
import createContentFile from '@/utils/media/createContentFile';
import { clearGeneration } from '@/utils/storage/generationStorage';
import { Screen } from '@/types';

import type { Deeplink } from '../types';
import { DeeplinkKind } from '../types';

type TaskDeeplink = Extract<Deeplink, { kind: DeeplinkKind.Task }>;

/**
 * Notification deeplink: fetch the generation result for a task id, persist
 * it to the store, and route the user into the lightbox result view.
 */
const taskHandler = async ({ taskId }: TaskDeeplink): Promise<void> => {
  setAppStore({ screen: Screen.Loading, hasAutoSelectedPreset: true });

  try {
    const media = await getMediaFromFlowId(taskId);
    if (!media) {
      setAppStore({ screen: Screen.Library, hasAutoSelectedPreset: false });
      return;
    }

    const driveFile = media.driveUid
      ? await getFile({ uid: media.driveUid })
      : null;

    const { contentFile } = createContentFile({
      media,
      fileName: driveFile?.name,
    });

    setAppStore({
      resultFile: media,
      screen: Screen.Files,
      ...(isDesktop && { rightPanelTab: RightPanelTab.Creations }),
      pendingLightboxFileId: contentFile.uuid,
      hasAutoSelectedPreset: false,
      isResultMode: true,
    });

    getAppStore().prependContentFile(contentFile);

    // The user has opened this result via the notification, so drop its
    // persisted crumb.
    await clearGeneration(taskId);
  } catch {
    setAppStore({ screen: Screen.Library, hasAutoSelectedPreset: false });
  }
};

export default taskHandler;
